import type { Contact } from '../domain/types.ts';
import type { Guide } from './types.ts';

// Local knowledge for a city-centre flat in Merchant City, Glasgow.
// Researched September 2026 — details marked "check" should be confirmed.

/** Suggested contacts the household can add in one tap. */
export const LOCAL_CONTACTS: Omit<Contact, 'id'>[] = [
  {
    role: 'vet',
    name: 'Queens Crescent Vets — Merchant City',
    address: '129 High Street, Glasgow G1 1PH',
    notes: 'Nearest practice (~5 min walk). Check phone number and hours on qcvc.co.uk.',
  },
  {
    role: 'emergency-vet',
    name: 'Emergency Vets Glasgow (24h)',
    phone: '0141 260 5555',
    address: 'WorkZone 85, 35 Rosyth Road, Glasgow G5 0YE',
    notes: 'Open 24 hours every day; takes public emergencies.',
  },
  {
    role: 'emergency-vet',
    name: 'Vets Now Glasgow (24/7)',
    phone: '0141 230 6575',
    address: '123–145 North Street, Glasgow G3 7DA',
    notes: 'Pet A&E, open 24/7. Number from vets-now.com — double-check it’s current.',
  },
];

export const LOCAL_GUIDE: Guide = {
  id: 'city-flat',
  title: 'A puppy in a Glasgow city-centre flat',
  category: 'start',
  minutes: 5,
  summary: 'No garden, a common stair and Merchant City on the doorstep: how to make toilet training, stairs, noise and walks work.',
  sections: [
    {
      heading: 'Toilet training without a garden',
      body: `Every toilet trip means getting down and outside, so timing matters even more than usual.

- **Pick one spot** close to the door that’s quiet and not used much by other dogs — ideally hard standing you can rinse. Go to the same spot every time; the smell helps him go.
- **Before his vaccinations are complete** (roughly 1–2 weeks after the 2nd jab — your vet gives the date), public ground can carry **parvovirus**, which survives for months. Keep to your low-traffic spot and don’t let him explore.
- **Time the route** from your door to the spot and enter it in Settings → Household → Home. Arlo HQ then reminds you that many minutes early.
- **Carry him** down the stairs or in the lift so there are no accidents on the common stair, and set him down right at the spot.
- Keep a **grab bag** by the door: lead, harness, poo bags, treats, keys, kitchen roll.
- One **puppy pad by the door** is a useful backup for emergencies and overnight — but it’s a bridge, not the plan. Outside is the goal.
- Straight out after waking, eating and play — no detours.`,
    },
    {
      heading: 'Stairs and lifts',
      body: `Stairs are hard on growing joints. **Carry him up and down until at least 12 weeks**, and keep carrying on the way down for longer — jumping down steps is the hardest on him. Once he does stairs, go slowly on a short lead.

Get him used to the **lift** early with treats (if you have one) — it’s a great socialisation item: noise, movement, strangers in a small space.`,
    },
    {
      heading: 'City socialisation — your superpower',
      body: `Merchant City is a socialisation goldmine. Before he can go on the ground, **carry him** (a sling helps) to watch from a distance:

- buses, taxis, bin lorries, sirens and roadworks
- Queen Street and Central stations, crowds and buskers
- bikes, scooters, prams, wheelchairs and people in hi-vis
- café and pub terraces — calm watching, lots of treats

Short and positive beats long and overwhelming. Log each one in Socialise.`,
    },
    {
      heading: 'Walks near Ingram Street',
      body: `- **Glasgow Green** (~10–12 min) is the nearest big grass, with open fields and wide paths; keep him on lead near roads and the People’s Palace. Heads-up: the winter funfair and ice rink are expected there from late November to early January — busy and noisy.
- **Clyde Walkway** (~5 min) for riverside lead walks.
- **Richmond Park** (~1.5 miles) and **Kelvingrove Park** (~2 miles) for longer outings later.
- **George Square** is under redevelopment into late 2026.
- Christmas markets (St Enoch Square from early November) mean crowds — good carried socialisation in small doses, not a place for a first proper walk.`,
    },
    {
      heading: 'Neighbours, noise and fireworks',
      body: `Tenement walls carry sound. Start **alone-time practice** in seconds, never let him cry it out, and give the neighbours a friendly heads-up that a puppy is arriving.

Private fireworks are allowed 6pm–11pm (until midnight on 5 November, 1am at New Year) and are common across the city from late October. Walk before dusk, close curtains, put the TV or music on, and start quiet sound-therapy tracks as soon as he’s home.`,
    },
    {
      heading: 'Scottish rules to know',
      body: `- **Pick up after him** — the fixed penalty under the Dog Fouling (Scotland) Act is **£80** (£100 if not paid in 28 days). Bagged waste can go in any public bin or your green bin.
- **Microchip** details must be in your name and kept up to date (Microchipping of Dogs (Scotland) Regulations 2016).
- **Collar and tag** with your name and address in public (Control of Dogs Order 1992).
- **Control of Dogs (Scotland) Act 2010:** councils can issue Dog Control Notices for dogs out of control — early recall and calm greetings matter.
- **Scottish Outdoor Access Code:** keep him under close control; use a short lead near livestock and during the bird nesting season (roughly April–July) on moors, grassland and shores.
- **Renting?** You need the landlord’s written consent. **Own the flat?** Check the Deed of Conditions for any pet clause.`,
    },
  ],
  sources: [
    { label: 'PDSA – parvovirus', url: 'https://www.pdsa.org.uk/pet-help-and-advice/pet-health-hub/conditions/parvovirus-in-dogs-and-puppies' },
    { label: 'Battersea – house training', url: 'https://www.battersea.org.uk/pet-advice/dog-care-advice/%E2%80%8Bhousetraining-your-dog' },
    { label: 'Scottish Outdoor Access Code – dog walking', url: 'https://www.outdooraccess-scotland.scot/practical-guide-all/dog-walkers/dog-walking' },
    { label: 'Scottish Government – fireworks law', url: 'https://www.gov.scot/publications/fireworks-the-law/' },
  ],
};
